import type { InvoiceResponseDto } from '@trellis/api'
import { describeReminderOffset } from '@trellis/roots/data'
import { Tooltip, TooltipContent, TooltipTrigger } from '@trellis/roots/tooltip'
import { MILLISECONDS_PER_DAY } from '@trellis/shared'
import { Bell, BellOff } from 'lucide-react'
import type { ReactNode } from 'react'

import { AMBER_CHIP_CLASSES } from '@/components/reminders'
import { useOrganizationToday } from '@/hooks/useOrganization'
import { formatCalendarDate } from '@/lib/format'
import { cn } from '@/lib/utils'

const SHORT_DATE: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
}

const daysBetween = (fromIso: string, toIso: string) =>
  Math.round(
    (Date.parse(`${toIso.slice(0, 10)}T00:00:00Z`) -
      Date.parse(`${fromIso.slice(0, 10)}T00:00:00Z`)) /
      MILLISECONDS_PER_DAY,
  )

function ChipWithTooltip({
  label,
  tooltip,
  className,
  children,
}: {
  label: string
  tooltip: string
  className?: string
  children: ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`${label}. ${tooltip}`}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
            className,
          )}
          onClick={(clickEvent) => clickEvent.stopPropagation()}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-60">{tooltip}</TooltipContent>
    </Tooltip>
  )
}

type InvoiceReminderChipProps = Pick<
  InvoiceResponseDto,
  'reminderState' | 'nextReminderEmailDate' | 'dueAt' | 'dismissedReminderCount'
>

/** Invoices table "Reminders" cell: what the next automatic reminder is, if any. */
export function InvoiceReminderChip({
  reminderState,
  nextReminderEmailDate,
  dueAt,
  dismissedReminderCount,
}: InvoiceReminderChipProps) {
  const today = useOrganizationToday()

  if (reminderState === 'PENDING') {
    return (
      <ChipWithTooltip
        label="On send"
        tooltip="Reminders are active. They start when you email this invoice."
        className="bg-muted text-muted-foreground"
      >
        <Bell className="size-3.5 [stroke-dasharray:3_3]" aria-hidden="true" />
        On send
      </ChipWithTooltip>
    )
  }

  if (reminderState === 'STOPPED') {
    return (
      <ChipWithTooltip
        label="Stopped"
        tooltip="No more reminder emails are scheduled for this invoice."
        className="text-muted-foreground px-0"
      >
        <BellOff className="size-3.5" aria-hidden="true" />
        Stopped
      </ChipWithTooltip>
    )
  }

  if (reminderState === 'OFF') {
    return (
      <ChipWithTooltip
        label="Off"
        tooltip="Automatic reminders are switched off for this invoice."
        className="text-muted-foreground px-0"
      >
        <BellOff className="size-3.5" aria-hidden="true" />
        Off
      </ChipWithTooltip>
    )
  }

  if (reminderState !== 'SCHEDULED' || !nextReminderEmailDate) {
    return <span className="text-muted-foreground">—</span>
  }

  const daysAway = daysBetween(today, nextReminderEmailDate)
  const isSoon = daysAway <= 1
  const label = isSoon
    ? `Reminder ${daysAway <= 0 ? 'today' : 'tomorrow'}`
    : formatCalendarDate(nextReminderEmailDate, SHORT_DATE)
  const offset = dueAt
    ? ` (${describeReminderOffset(daysBetween(dueAt, nextReminderEmailDate))})`
    : ''
  const dismissedNote =
    dismissedReminderCount > 0 ? ` ${dismissedReminderCount} dismissed.` : ''

  return (
    <ChipWithTooltip
      label={label}
      tooltip={`Next email: ${formatCalendarDate(nextReminderEmailDate, SHORT_DATE)}, 9:00 am${offset}.${dismissedNote}`}
      className={isSoon ? AMBER_CHIP_CLASSES : 'bg-muted text-muted-foreground'}
    >
      <Bell className="size-3.5" aria-hidden="true" />
      {label}
    </ChipWithTooltip>
  )
}
